import React, { useEffect, useState } from "react";
import axios from "axios";
import safeToast from "utils/safeToast";
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import {
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    Autocomplete,
    Box,
} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AddIcon from "@mui/icons-material/Add";
import { error } from "ajv/dist/vocabularies/applicator/dependencies";
import { API_ENDPOINTS } from "config/api";
import { decryptText } from "config/encryption";

const StudentExpertise = () => {
    const [expertiseList, setExpertiseList] = useState([]);
    const [masterExpertise, setMasterExpertise] = useState([]);
    const [selectedExpertiseId, setSelectedExpertiseId] = useState(null);
    const [selectedExpertiseNotes, setSelectedExpertiseNotes] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [editExpertiseId, setEditExpertiseId] = useState(0);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newExpertiseDialogOpen, setNewExpertiseDialogOpen] = useState(false);
    const [newExpertiseName, setNewExpertiseName] = useState("");
    const [newExpertiseDescription, setNewExpertiseDescription] = useState("");
    const [selectedIsActive, setSelectedIsActive] = useState(true);

    const fetchExpertise = async (studentId, token) => {
        const res = await axios.get(
            API_ENDPOINTS.PROFILE.GET_STUDENT_EXPERTISE(studentId),
            { headers: { Authorization: `Bearer ${token}` } }
        );
        setExpertiseList(res.data);
    };

    const fetchMasterExpertise = async (token) => {
        const res = await axios.get(API_ENDPOINTS.PROFILE.GET_MASTER_EXPERTISE,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        setMasterExpertise(res.data);
    };

    useEffect(() => {
        const init = async () => {
            try {
                const userDetails = localStorage.getItem("UserLogDetails");
                if (!userDetails) throw new Error("No user details");
                const decryptedUser = decryptText(userDetails);
                const parsedUser = JSON.parse(decryptedUser);
                const { StudentId, Token } = parsedUser;
                await fetchExpertise(StudentId, Token);
                await fetchMasterExpertise(Token);
            } catch (error) {
                safeToast.error("Session expired or failed to fetch.");
                localStorage.clear();
                window.location.href = "/BasicAuth";
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    const openAddDialog = () => {
        setEditMode(false);
        setEditExpertiseId(0);
        setSelectedExpertiseId(null);
        setSelectedExpertiseNotes("");
        setDialogOpen(true);
    };

    const openEditDialog = (exp) => {
        setEditMode(true);
        setEditExpertiseId(exp.id);
        setSelectedExpertiseId({ id: exp.expertiseId, expertiseName: exp.expertiseName });
        setSelectedExpertiseNotes(exp.notes || "");
        setDialogOpen(true);
    };

    const handleSaveExpertise = async () => {
        const userDetails = JSON.parse(decryptText(localStorage.getItem("UserLogDetails")));
        const { StudentId, Token } = userDetails;

        if (!selectedExpertiseId || !selectedExpertiseId.id) {
            safeToast.warning("Please select an expertise.");
            return;
        }

        const payload = {
            id: editMode ? editExpertiseId : 0,
            studentId: StudentId,
            expertiseId: selectedExpertiseId.id,
            isActive: selectedIsActive ? 1 : 0,
            notes: selectedExpertiseNotes,
            createdUserId: 1,
            modifiedUserId: 0,
        };

        try {
            await axios.post(API_ENDPOINTS.PROFILE.MERGE_STUDENT_EXPERTISE, payload, {
                headers: { Authorization: `Bearer ${Token}` },
            });
            safeToast.success(editMode ? "Expertise updated." : "Expertise added.");
            await fetchExpertise(StudentId, Token);
            setDialogOpen(false);
        } catch (err) {
            safeToast.error("Failed to save expertise.");
        }
    };

    const handleAddNewExpertise = async () => {
        const userDetails = JSON.parse(decryptText(localStorage.getItem("UserLogDetails")));
        const { Token } = userDetails;

        const payload = {
            id: 0,
            expertiseName: newExpertiseName,
            description: newExpertiseDescription,
            modifiedUserId: 0,
        };

        try {
            await axios.post(API_ENDPOINTS.PROFILE.MERGE_MASTER_EXPERTISE, payload, {
                headers: { Authorization: `Bearer ${Token}` },
            });
            safeToast.success("New expertise added.");
            await fetchMasterExpertise(Token);
            setNewExpertiseDialogOpen(false);
            setNewExpertiseName("");
            setNewExpertiseDescription("");
        } catch (err) {
            safeToast.error("Failed to add new expertise.");
        }
    };

    if (loading) {
        return (
            <MDBox display="flex" justifyContent="center" py={3}>
                <CircularProgress />
            </MDBox>
        );
    }

    return (
        <MDBox py={2} px={3}>
            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                <MDBox
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    mx={2}
                    mt={-3}
                    p={2}
                    mb={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <MDTypography variant="h6" color="white">
                        My Expertise
                    </MDTypography>
                    <Tooltip title="Add">
                    <IconButton onClick={openAddDialog} color="inherit">
                        <Add sx={{ color: "#fff" }} />
                    </IconButton>
                    </Tooltip>
                </MDBox>

                <CardContent sx={{ backgroundColor: "#fafafa" }}>
                    <Grid container spacing={2}>
                        {expertiseList.map((exp) => (
                            <Grid item xs={12} key={exp.id}>
                                <MDBox
                                    p={2}
                                    border="1px solid #ddd"
                                    borderRadius={2}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Box>
                                        <MDTypography variant="subtitle2">{exp.expertiseName}</MDTypography>                                 
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <Tooltip title={exp.isActive ? "Active" : "Inactive"}>
                                            {exp.isActive ? (
                                                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                            ) : (
                                                <CancelIcon color="error" sx={{ mr: 1 }} />
                                            )}
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                        <IconButton onClick={() => openEditDialog(exp)} color="info">
                                            <Edit />
                                        </IconButton>
                                        </Tooltip>
                                    </Box>
                                </MDBox>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            {/* Add/Edit Expertise Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editMode ? "Update Expertise" : "Add Expertise"}</DialogTitle>
                <DialogContent dividers>
                    <Autocomplete
                        fullWidth
                        options={[...masterExpertise, { id: -1, expertiseName: "+ Add New Expertise..." }]}
                        getOptionLabel={(option) => option.expertiseName}
                        value={selectedExpertiseId}
                        onChange={(e, newValue) => {
                            if (newValue?.id === -1) {
                                setNewExpertiseDialogOpen(true);
                            } else {
                                setSelectedExpertiseId(newValue);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Select Expertise" margin="normal" />
                        )}
                    />

                    <TextField
                        label="Notes"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={3}
                        value={selectedExpertiseNotes}
                        onChange={(e) => setSelectedExpertiseNotes(e.target.value)}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={selectedIsActive}
                                onChange={(e) => setSelectedIsActive(e.target.checked)}
                            />
                        }
                        label="Active"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>

                <DialogActions>
                    <MDButton onClick={() => setDialogOpen(false)} variant="outlined" color="secondary">
                        Cancel
                    </MDButton>
                    <MDButton onClick={handleSaveExpertise} variant="gradient" color="info">
                        {editMode ? "Update" : "Add"}
                    </MDButton>
                </DialogActions>
            </Dialog>


            {/* New Expertise Dialog */}
            <Dialog open={newExpertiseDialogOpen} onClose={() => setNewExpertiseDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New Expertise</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Expertise Name"
                        fullWidth
                        margin="normal"
                        value={newExpertiseName}
                        onChange={(e) => setNewExpertiseName(e.target.value)}
                    />
                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        margin="normal"
                        value={newExpertiseDescription}
                        onChange={(e) => setNewExpertiseDescription(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setNewExpertiseDialogOpen(false)} variant="outlined" color="secondary">
                        Cancel
                    </MDButton>
                    <MDButton onClick={handleAddNewExpertise} variant="gradient" color="info">
                        Save
                    </MDButton>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
};

export default StudentExpertise;
